import { Dayjs } from "dayjs";
import pluralize from "pluralize";
import { PlanType } from "../types/planning/plan.type";
import { DaylightDateFormat } from "./DaylightDateFormat";

export const getLowerAndCapitalizedCase = (word: string) => {
  const lowerCase = word.toLowerCase();
  const capitalized = lowerCase.charAt(0).toUpperCase() + lowerCase.slice(1);
  return { lowerCase, capitalized };
};

export const unavailableText = "Unavailable";

export const displayMessage = {
  default: {
    noResults: "Uh oh! We couldn't find any results for you.",
    noResultsAndSearchAgain:
      "Sorry, no results found. Please try a different search.",
    emptyFilterResults:
      "Try adjusting your search or filter to find what you're looking for.",
    deleteFromSelection: "Delete from selection.",
    pageNotFound: "Unlike the shipments we handle, this page is missing!",
    maintenanceTitle: "Under Maintenance",
    maintenanceMessage:
      "We're currently performing scheduled maintenance. Please check back shortly.",
    internalError:
      "An internal error occurred. Please try reloading the page or try again later.",
    unauthorizedError: (email: string) =>
      `Sorry, your account ${email} doesn’t have permission to perform this action. If you believe you need access, please contact your administrator for further assistance.`,
    recordInactive: (recordName: string) => {
      const { lowerCase, capitalized } = getLowerAndCapitalizedCase(recordName);
      return `${capitalized} is inactive. Contact Safety Dept. or select new ${lowerCase}`;
    },
    recordRemoved: (recordName: string) => {
      const { lowerCase, capitalized } = getLowerAndCapitalizedCase(recordName);
      return `${capitalized} is unavailable. Select new ${lowerCase}`;
    },
  },
  tripList: {
    noTrips: "No trips found",
    noTripsDescription: "Try adjusting your date range or filters.",
    loadError: "Failed to load trip list",
    loadErrorDescription: "Please try reloading the page.",
  },
  equipmentInventory: {
    statusNotEmpty:
      "The trailer needs to be in EMTY status to be repositioned.",
  },
  linehaul: {
    ratesAndAccessorial: {
      mCodes: {
        allActiveVendors: (count: number) => `All Active M Vendors (${count})`,
        viewAllTolls: "See all Tolls",
        viewExpiredRates: "View expired rates",
        addNewRate: "Add New Rate",
        editMRate: "Edit M Rate",
        saveAndClose: "Save & Close",
        activeRatesCannotBeEdited:
          "Active rates cannot be edited, they can only be expired.",
        addExpirationDate: "Add expiration date",
        originSicListPlaceholder: "M Codes Origin SIC list content",
        viewLanesAndVendors: "View lanes and vendors",
        ratesCount: (count: number) =>
          `${count} ${count === 1 ? "Rate" : "Rates"}`,
        mCarriersFilterLabel: "M Carriers",
        noRatesFound: "No rates found",
        downloadAllMRates: "Download All M Rates",
        deleteRate: "Delete",
        editRate: "Edit",
        editVendors: "Edit",
        vendorsCount: (count: number) => `Vendors (${count})`,
        lanesCount: (count: number) => `Lanes (${count})`,
        lanesCountEditMode: (activeCount: number, totalCount: number) =>
          `Lanes (${activeCount} actives of ${totalCount})`,
        editLanes: "Edit Lanes",
        cancel: "Cancel",
        saveChanges: "Save Changes",
        originFilterLabel: "Origin",
        destinationFilterLabel: "Destination",
        billableMilesHeader: "Billable Miles",
        originHeader: "Origin",
        destinationHeader: "Destination",
        showsOnlyLanesWithVendorConflict:
          "Shows only lanes with vendor conflict",
        addVendorModalTitle: (serviceType: string, rateDisplay: string) =>
          `Add New Vendor - ${serviceType} ${rateDisplay}`,
        addVendorModalInstruction: "Please add the active vendors",
        addVendorModalSearchPlaceholder: "Search",
        addVendorModalShowVendorConflicts: "Show vendor conflicts",
        addVendorModalActiveVendorsCount: (active: number, total: number) =>
          `Active Vendors (${active} of ${total})`,
        addVendorModalVendorColumn: "Vendor",
        addVendorModalMenuAddNewVendor: "Add new vendor",
        /** Mid-sentence for rich vendor conflict tooltip (see VendorActiveOnRateTooltip). */
        addVendorModalVendorActiveOnRateMidPhrase: "is currently active on",
        addVendorModalSave: "Save",
        noActiveVendors: "No active vendors found",
      },
      tolls: {
        pageTitle: "Tolls",
        error: "Failed to load tolls",
        searchPlaceholder: "Search Org or Dest",
        addNewToll: "Add New Toll",
        totalLanes: (count: number) => `${count} Lanes`,
        editToll: "Edit toll",
        viewExpiredTolls: "View expired tolls",
        expiredTollsError: "Failed to load expired tolls",
        filterOrigin: "Origin",
        filterDestination: "Destination",
        filterExpirationDate: "Expiration Date",
        emptyState: "No tolls found",
        emptyStateDescription: "Try adjusting your search or filters",
      },
      lCodes: {
        error: "Failed to load L Codes",
        emptyState: "No L Codes found",
        carrierNotFound: "Carrier not found",
      },
    },
    lCodeCarrierProfile: {
      noAddendums: "No Addendums",
      originSicsError: "Failed to load origin SIC",
      originSicsEmpty: "No origin SICs found",
      destinationsList: {
        error: "Failed to load destination SIC",
      },
    },
    unmerge: {
      success: (unmergedLanes: string[], primaryLane?: string) =>
        `${unmergedLanes.map((lane) => `"${lane}"`).join(", ")} unmerged${primaryLane ? ` from "${primaryLane}"` : ""}`,
      error: {
        default: "Uh oh! There was an issue unmerging these lanes",
      },
    },
    merge: {
      success: (primaryLane: string, secondaryLanes: string[]) =>
        `${secondaryLanes.map((lane) => `"${lane}"`).join(", ")} merged into ${primaryLane} lane`,
      error: {
        default: "Uh oh! There was an issue merging these lanes",
        selectAtLeastTwo: "Select at least 2 lanes to merge",
        selectPrimaryLane: "Select the primary lane to merge",
        notEnabled: "Merging lanes is not currently enabled",
      },
    },
    masterSchedule: {
      error: "Uh oh! There was an issue fetching the schedules.",
      empty: 'Click "Create Plan" to get started.',
      filterEmpty: "Uh oh! We couldn't find any schedules.",
    },
    templates: {
      error: "Uh oh! There was an issue fetching the templates.",
      empty: 'Click "Create Template" to get started.',
      filterEmpty: "Uh oh! We couldn't find any templates.",
    },
    template: {
      error: "Uh oh! There was an issue fetching the template.",
      empty: 'Click "New Entry" to get started.',
      filterEmpty: "Uh oh! We couldn't find any schedules.",
    },
    loadBoard: {
      error: "Uh oh! There was an issue fetching loadboard.",
      empty: "Uh oh! We couldn't find any service centers.",
    },
    loadPlanReport: {
      empty: "There is no data for given date",
      emptySecondary: "Please select a different date",
      emailSubject: (sicCode: string) => `${sicCode} - Load Plan Report`,
      emailRecipients: "support@example.com",
      emailBodyTemplate: `
        Hi, I hope this message finds you well. 

        Please find the attached report as requested. 
        Let me now if you need any further information or clarification.

        Best regards, 

      `,
    },
    serviceCenter: {
      error: "Uh oh! There was an issue fetching this service center.",
      empty: "Uh oh! We couldn't find any lanes.",
    },
    tripList: {
      error: "Uh oh! There was an issue fetching the trip list.",
      empty: "Uh oh! We couldn't find any trips.",
      updateTripNote: (tripNumber: number) =>
        `Note for trip ${tripNumber} was updated`,
    },
    ucccOfd: {
      shipmentsError: "Uh oh! There was an issue loading UC/CC OFD shipments.",
      editOwnerLoadError: "Failed to load owners. Please try again.",
      shipmentsEmpty: "No UC/CC OFD shipments found for this date.",
      noFilterResults: "No shipments match the selected filters",
      noFilterResultsSecondary:
        "Try adjusting your filter selections or clear all filters",
      serviceFailureRecapReport: {
        invalidParams:
          "Start and end dates are required to generate this report. Please go back and try again.",
        successMessage: "100% On-Time Woohoo!",
      },
    },
    tripsDashboard: {
      title: "Trips Dashboard",
      searchResults: (query: string) => `Search Results '${query}'`,
      search: {
        minCharactersError: "Minimum 3 characters required",
        tripNumberMinCharactersError: "Minimum 5 characters required",
        emptySearchError: "Please enter a search value",
        emptySearchPlaceholder: "Search requires at least 3 characters",
        emptySearchSecondary: "Please enter 3 or more characters to search",
      },
    },
    tripDocuments: {
      empty: "No documents available",
      emptySecondary: "There are no documents for this trip yet.",
      error: "Uh oh! There was an issue fetching documents.",
    },
    tripNotes: {
      empty: "No notes available",
      emptySecondary: "There are no notes for this trip yet.",
      create: {
        success: "Note created",
        failed: "Failed to create note",
      },
      update: {
        success: "Note updated",
        failed: "Failed to update note",
      },
      delete: {
        success: "Note deleted",
        failed: "Failed to delete note",
      },
    },
    shipmentNotes: {
      empty: "No notes available",
      emptySecondary: "There are no notes for this shipment yet.",
      create: {
        success: "Note created",
        failed: "Failed to create note",
      },
      update: {
        success: "Note updated",
        failed: "Failed to update note",
      },
      delete: {
        success: "Note deleted",
        failed: "Failed to delete note",
      },
    },
    tripHistory: {
      empty: "No status history available",
      emptySecondary:
        "Uh oh! We couldn't find any status history for this trip yet.",
      error: "Uh oh! There was an issue loading status history.",
      delete: {
        success: "Trip history status deleted successfully",
        failed: "Failed to delete trip history status",
      },
    },
    transitDashboard: {
      recentUpdates: {
        title: "Recent Updates",
        empty: "No recent updates",
        emptySecondary: "There are no recent trip updates at this time",
        error: "Uh oh! There was an issue loading recent updates.",
      },
      shipments: {
        error: "Failed to load shipments.",
        empty: "Uh oh! We couldn't find any shipments.",
      },
      bulkRequest: {
        empty: "Uh oh! We couldn't find any trips available",
        emptySecondary:
          "There are no active trips for this carrier at this time.",
      },
      notifications: {
        error: "Uh oh! There was an issue loading notifications.",
        empty: "No notifications",
        emptySecondary: "You're all caught up. No alerts at this time.",
        emptyFilterAll: "No alerts",
        emptyFilterRead: "No read alerts",
        emptyFilterUnread: "No unread alerts",
        contactCarrierEmailCc: "lhtracking@dylt.com,linehaul@dylt.com",
        contactCarrierEmailSubject: (
          tripNumber: number,
          bookingNumber?: string
        ) =>
          `Trip ${tripNumber}${bookingNumber ? `, Booking ${bookingNumber}` : ""}`,
        contactCarrierEmailBody:
          "Hi Team,\n\nThe mentioned trip has now tracked in the same city/state for 2 hours now. Have they encountered any delays?\n\nThank you,",
      },
    },

    rightPanel: {
      notes: {
        delete: "This action cannot be undone.",
      },
      shipments: {
        error: "Uh oh! There was an issue fetching the shipments.",
        empty: "Uh oh! We couldn't find any shipments.",
      },
      schedules: {
        error: "Uh oh! There was an issue fetching the schedules.",
        empty: "Uh oh! We couldn't find any schedules.",
      },
      trips: {
        error: "Uh oh! There was an issue fetching the trips.",
        empty: "Uh oh! We couldn't find any trips.",
      },
      tripDetails: {
        error: "Uh oh! There was an issue fetching the trip details.",
      },
    },
    trips: {
      reinstateSchedule: {
        info: "You can only reinstate 1 cancelled trip at a time.",
      },
      error: "Uh oh! There was an error fetching this trip.",
    },
    copyURLToClipboard: "URL copied to clipboard",
    cancellationsReport: {
      info: "Generating report...",
      error: "Uh oh! There was an error fetching the cancellations report.",
      dateValidationError: "Start Date must not be greater than End Date",
    },
  },
  outbound: {
    doorRules: {
      duplicateAndEmpty:
        "Duplicate and empty rules detected. Please review highlighted rules.",
      duplicate: "Duplicate rules detected. Please review highlighted rules.",
      empty: "Empty rules detected. Please review highlighted rules.",
    },
    staticLoadTemplates: {
      filterEmpty: "Uh oh! We couldn't find any static load templates.",
      empty: "There are no static load templates registered for this SIC.",
    },
    presetLanes: {
      filterEmpty: "Uh oh! We couldn't find any preset lanes.",
      empty: "There are no preset lanes registered for this SIC.",
    },
    freightDetails: {
      error: "Uh oh! There was an issue fetching Freight Details.",
      filterEmpty: "Uh oh! We couldn't find any Freight Detail.",
      empty: "There are no freight details registered for this SIC.",
    },
    urgentAndCriticalCare: {
      filterEmpty: "Uh oh! We couldn't find any shipment with UC and CC.",
      empty: "There are no shipments with UC and CC registered for this SIC.",
    },
    trailerHub: {
      noRecords: "Uh oh! We couldn't find any trailers.",
      empty: "There are no trailers registered for this SIC.",
    },
    trailerHubShipments: {
      noRecords: "Uh oh! We couldn't find any shipment for this trailer.",
      empty: "There are no shipments for this trailer.",
    },
    moveShipments: {
      lane: {
        success: (items: number, loadTo: string) =>
          `${pluralize("shipment", items, true)} moved to ${loadTo} lane.`,
        defaultSuccess: (items: number) =>
          `${pluralize("shipment", items, true)} moved to lane.`,
        error: (items: number) =>
          `Failed to move ${pluralize("shipment", items)} to the lane.`,
      },
      trap: {
        success: (items: number, trapName: string) =>
          `${pluralize("shipment", items, true)} moved to ${trapName}.`,
        defaultSuccess: (items: number) =>
          `${pluralize("shipment", items, true)} moved to trap.`,
        error: (items: number) =>
          `Failed to move ${pluralize("shipment", items)} to the trap.`,
      },
      door: {
        success: (items: number, door: number) =>
          `${pluralize("shipment", items, true)} moved to door ${door}.`,
        defaultSuccess: (items: number) =>
          `${pluralize("shipment", items, true)} moved to door.`,
        error: (items: number) =>
          `Failed to move ${pluralize("shipment", items)} to the door.`,
      },
      bay: {
        success: (items: number, bay: number) =>
          `${pluralize("shipment", items, true)} moved to bay ${bay}.`,
        defaultSuccess: (items: number) =>
          `${pluralize("shipment", items, true)} moved to bay.`,
        error: (items: number) =>
          `Failed to move ${pluralize("shipment", items)} to the bay.`,
      },
    },
    splitLane: {
      success: (destinationSic: string, originSic: string) =>
        `${destinationSic} lane split from ${originSic}.`,
      error: (destinationSic: string) =>
        `Failed to split ${destinationSic} lane.`,
    },
    addDoorToLane: {
      success: (doorNumber: number) => ` Door ${doorNumber} added.`,
      error: (laneSic: string) => `Failed to add door to ${laneSic} lane.`,
      allowShipments:
        "Only shipments with this tag will be auto-assigned to this door. All others must be added manually.",
      reassignShipments:
        "Shipments not yet loaded with selected shipment tags in this lane will be reassigned to this door.",
    },
    editDoorDetails: {
      success: (doorNumber: number) => ` Door ${doorNumber} updated.`,
      error: (doorNumber: number) =>
        `Failed to edit Door ${doorNumber} details.`,
      allowShipments:
        "Only shipments with this tag will be auto-assigned to this door. All others must be added manually.",
      reassignShipments:
        "Shipments not yet loaded with selected shipment tags in this lane will be reassigned to this door.",
    },
    removeDoorFromLane: {
      success: (doorNumber: number, laneSic: string, isTrap = false) =>
        ` Door ${doorNumber} removed from ${laneSic}${isTrap ? "" : " lane"}.`,
      error: (laneSic: string) => `Failed to remove door from ${laneSic} lane.`,
    },
    shipmentTransitEstimate: {
      success: () => "All shipments will make service.",
      error: (items: number) =>
        `Your selection will cause ${pluralize(
          "shipment",
          items,
          true
        )} to miss service.`,
      warning:
        "Unable to determine transit time. You may divert shipments to your chosen location, select an alternative, or cancel diversion.",
    },
    reconcileShipments: {
      success: () => "All shipments will make service.",
      error: (items: number) =>
        `${pluralize("shipment", items, true)} will miss service.`,
    },
    serviceDueError: "Contains shipments that will fail service.",
    serviceDueShipmentError: "Shipment will fail service.",
    routeNotFound: "System error: Route not found",
  },
  inbound: {
    changePlanDetails: {
      confirmTitle: "Review plan change",
      confirmText: (plannedShipmentsCount: number) =>
        `You have selected ${pluralize(
          "shipment",
          plannedShipmentsCount,
          true
        )} already part of a plan. Do you want to proceed?`,
    },
    planning: {
      lockRoutes: {
        invalidSelection: "Select locked or unlocked routes only",
      },
      loadingTitle: (numRoutes: number, isChecked: boolean) =>
        `${isChecked ? "Unlocking " : "Locking "} ${pluralize(
          "route",
          numRoutes
        )}...`,
      statusChanged: (routeName: string, type: PlanType, newStatus: string) =>
        `${routeName} ${type === "delivery_route" ? "route" : "trap"} changed to ${newStatus}`,
      statusChangeFailed: (
        routeName: string,
        type: PlanType,
        newStatus: string
      ) =>
        `${routeName} ${type === "delivery_route" ? "route" : "trap"} failed to transition to ${newStatus}`,
      bulkStatusChangedSuccessfully: (
        routesTrapsCount: number,
        planType: PlanType
      ) =>
        `${routesTrapsCount} ${pluralize(
          planType === "delivery_route" ? "route" : "trap",
          routesTrapsCount
        )} updated`,
      bulkStatusChangedPartialSuccess: (
        successRoutesCount: number,
        failRoutesCount: number,
        successTrapsCount: number,
        failTrapsCount: number
      ) =>
        `${successRoutesCount} ${pluralize(
          "route",
          successRoutesCount
        )} updated. ${failRoutesCount} ${pluralize(
          "route",
          failRoutesCount
        )} failed. \n` +
        `${successTrapsCount} ${pluralize(
          "trap",
          successTrapsCount
        )} updated. ${failTrapsCount} ${pluralize(
          "trap",
          failTrapsCount
        )} failed`,
      bulkStatusChangedPartialSuccessRoute: (
        successRoutesCount: number,
        failRoutesCount: number
      ) =>
        `${successRoutesCount} ${pluralize(
          "route",
          successRoutesCount
        )} updated. ${failRoutesCount} ${pluralize(
          "route",
          failRoutesCount
        )} failed. \n`,
      bulkStatusChangedPartialSuccessTrap: (
        successTrapsCount: number,
        failTrapsCount: number
      ) =>
        `${successTrapsCount} ${pluralize(
          "trap",
          successTrapsCount
        )} updated. ${failTrapsCount} ${pluralize(
          "trap",
          failTrapsCount
        )} failed`,
      bulkStatusChangedFailed: () => "Failed to update routes and traps",
      statusesChanged: (
        numRoutes: number,
        numTraps: number,
        newStatus: string
      ) => {
        const routesText =
          numRoutes > 0 ? `${pluralize("route", numRoutes, true)}` : "";
        const trapsText =
          numTraps > 0 ? `${pluralize("trap", numTraps, true)}` : "";
        const separator = numRoutes > 0 && numTraps > 0 ? " and " : "";
        return `${routesText}${separator}${trapsText} changed to ${newStatus}`;
      },
    },
    dnd: {
      notAvailableForMassage:
        "The destination route must go back to CLDK status before proceeding with the action.",
      mapReorder: (routeId: string, markerStopSeq: number) =>
        `Reorder to <b>${routeId} Route</b> and set Stop ${markerStopSeq}`,
      mapAssignToLastStop: (routeId: string) =>
        `Assign to <b>${routeId} Route</b> and set as stop or make last`,
      mapAssign: (routeId: string, markerStopSeq: number) =>
        `Assign to <b>${routeId} Route</b> and set Stop ${markerStopSeq}`,
      mapAssignAndAutoSequence: (routeId: string) =>
        `Assign to <b>${routeId} Route</b> and auto-sequence`,
    },
  },
  employees: {
    sidebarLabel: "Biometrics",
    title: "Users",
    searchPlaceholder: "Search by name or ID",
    filterAll: "All",
    filterDockWorker: "Dockworker",
    filterCompanyDriver: "Company Driver",
    filterCSR: "Customer Service Reps",
    filterServiceCenterManagement: "Service Center Management",
    filterIndependentContractor: "Independent Contractors",
    columns: {
      name: "Name",
      employeeId: "Employee ID",
      role: "Role",
      location: "Location",
      kronosId: "Kronos ID",
    },
    empty: "No users found.",
    error: "Failed to load users.",
    detail: {
      fullName: "Full Name",
      employeeId: "Employee ID",
      job: "Job",
      faceIdTitle: "Profile Face ID",
      faceIdDescription:
        "Quickly and safely access your account by using Face ID.",
      photoTitle: "Photo",
      photoDescription:
        "Take a live photo to enable Face ID. This will complete your profile setup.",
      removeFaceIdTitle: "Remove Face ID",
      removeFaceIdMessage: (name: string) =>
        `Are you sure you want to remove ${name}'s Face ID?`,
      removeFaceIdConfirm: "Yes, remove it",
      removeFaceIdCancel: "No, keep it",
      faceIdRemovedSuccess: "Face ID removed successfully",
      faceIdUploadedSuccess: "Face ID photo uploaded successfully",
      cameraPermissionError:
        "Permission denied or error accessing camera, please give access and refresh the page",
      webcamTitle: "Take a Photo",
      webcamInstruction: "Align your face in the frame",
      webcamTips: "Remove hats, sunglasses, or anything obstructing your face",
      webcamRetake: "Retake",
      webcamUpload: "Upload",
      webcamErrorPrefix: "We couldn't use this photo for Face ID because",
      resetFaceId: "Reset Face ID",
      removeFaceId: "Remove",
      loadError: "Failed to load user details.",
      invalidImageFormat: "Invalid image format. Please try again.",
    },
  },
  settings: {
    serviceHolidays: {
      error: "Uh oh! There was an issue fetching the service holidays.",
      empty: "No service holidays found.",
      pageTitle: "Service Holidays",
      pageSubtitle:
        "View, import, and manage service holidays across all years",
      calendar: {
        gridLabel: (year: string) => `Service holidays calendar for ${year}`,
        monthGridLabel: (monthName: string) => `${monthName} holidays calendar`,
        noHolidaysInMonth: "No holidays this month",
        holidayCellLabel: (count: number, monthDay: string) =>
          `${count} ${pluralize("holiday", count)} on ${monthDay}`,
        hiddenHolidaysLabel: (count: number) =>
          `${count} more ${pluralize("holiday", count)}`,
      },
      detailPanel: {
        title: "Holiday Details",
        closeAriaLabel: "Close holiday details",
        editButton: "Edit",
        editAriaLabel: (name: string) => `Edit ${name}`,
        eolSectionTitle: (count: number) => `EOL Service Centers (${count})`,
        eolFilterAll: "All",
        eolFilterAriaLabel: "Filter by carrier",
        eolRowAriaLabel: (scCode: string, name: string) => `${scCode} ${name}`,
        carrierChipAriaLabel: (code: string, name: string) =>
          `Carrier ${code} ${name}`,
        noEolServiceCenters: "No EOL service centers found.",
        emptyTitle: "No holiday selected",
        emptySubtitle: "Click a holiday to view its details.",
      },
      deleteDialog: {
        title: "Delete Service Holiday",
        message: (name: string, date: string) =>
          `Are you sure you want to delete the service holiday "${name} (${date})"? This will remove the holiday for all selected Carriers and EOLs. This action cannot be undone.`,
        confirmButton: "Delete",
        confirmButtonLoading: "Deleting...",
      },
    },
    groups: {
      deleteConfirmDialog: {
        question: "Are you sure you want to delete the group? ",
        highlight: "This action cannot be undone.",
        warning:
          "Deleting this group will remove all associated permissions for users within this group. Affected users will lose access to features and functionalities immediately upon deletion. ",
      },
    },
    linehaul: {
      regions: {
        error: "Uh oh! There was an issue fetching the regions.",
        empty: 'Click "Create Region" to get started.',
      },
      sics: {
        error: "Uh oh! There was an issue fetching the sics.",
      },
      contacts: {
        error: "Uh oh! There was an issue fetching the sic contacts.",
        empty: "There is not registered contacts.",
        addContact: {
          success: "Contact added",
          error: "Uh oh! There was an issue adding the contact.",
        },
        updateContact: {
          success: "Contact updated",
          error: "Uh oh! There was an issue updating the contact.",
        },
        deleteContact: {
          success: "Contact deleted",
          error: "Uh oh! There was an issue deleting the contact.",
        },
      },
    },
  },
  backOffice: {
    pickupEntry: {
      freightOptionsLoadError: "Failed to load freight options",
      freightOptionsLoadErrorDescription: "Please try reloading the page",
      cancelPickupDialog: {
        title: "Cancel Pickup",
        close: "Close dialog",
        warningBeforePickup: "You are about to cancel pickup",
        warningAfterPickup:
          ". This action cannot be undone. Please provide a reason for cancellation.",
        reasonLabel: "Reason for Cancellation",
        reasonRequired: "Reason for cancellation is required",
        goBack: "Go Back",
        confirm: "Cancel Pickup",
      },
      documentUpload: {
        fileSizeError: (maxSizeInMB: number) =>
          `File size must be less than ${maxSizeInMB}MB`,
        fileTypeError:
          "Please select a valid file type (PDF, DOC, DOCX, XLSX, CSV, JPG, JPEG, PNG)",
      },
      confirmPickupChangesDialog: {
        title: "Confirm Pickup Changes",
        close: "Close dialog",
        warningBeforePickup: "You are about to save changes to pickup",
        warningAfterPickup: ". Please provide a reason for this modification.",
        reasonLabel: "Reason for Changes",
        reasonRequired: "Reason for changes is required.",
        cancel: "Cancel",
        saveChanges: "Save Changes",
      },
      validation: {
        zipCodeRequired: "Zip/Postal code is required",
        invalidZipCodeFormat:
          "Invalid zip/postal code format. Must be valid for US (12345 or 12345-6789), Canada (A1A 1A1), or Mexico (12345)",
      },
      summaryMoreMenu: {
        viewChangeLog: "View Change Log",
        duplicatePickup: "Duplicate Pickup",
        sendBol: "Send BOL",
        sendShippingLabels: "Send Shipping Labels",
        blindShipmentForm: "Blind Shipment Form",
        cancelPickup: "Cancel Pickup",
      },
      /** Pickup details / summary PDF letterhead (print-only block). */
      printLetterhead: {
        pickupDetailsHeading: "Pickup Details",
        companyName: "Daylight Transport",
        logoAlt: "Daylight Transport",
        companyContactLines: [
          {
            line: "5660 Katella Avenue Suite 250, Cypress CA 90630",
            emphasis: false,
          },
          {
            line: "Phone: 310-507-8200   (800) 468-9999",
            emphasis: false,
          },
          { line: "Fax: (310) 507-8310", emphasis: true },
        ],
      },
    },
    rateQuote: {
      shipperMismatchDialog: {
        message:
          "Selected quotes have different shippers. Please select quotes with the same shipper to create a pickup.",
        close: "Close shipper mismatch dialog",
      },
    },
    billing: {
      dashboardTitle: "Dashboard",
      timezoneEst: "EDT",
      timezoneCst: "CDT",
      timezoneMst: "MDT",
      timezonePst: "PDT",
      timeRemainingLabel: "Time Remaining",
      pipelineEfficiencyTitle: "Pipeline Efficiency",
      billPerHour: "Bill Per Hour",
      turnaroundTime: "Turnaround Time",
      percentCompleted2200Pst: "% completed (2200 PDT)",
      completionRate: "Completion Rate",
      billingAccuracyTitle: "Billing Accuracy",
      accuracy: "Accuracy",
      errorRate: "Error Rate",
      reworkRate: "Rework Rate",
      errorRateCost: "Error Rate Cost",
      accuracyOfAcc: "Accuracy of ACC",
      pickupRequestsTitle: "Pickup Requests",
      companyBills: "Company Bills",
      agentsBills: "Agents Bills",
      puConfirmed: "PU Confirmed",
      bolsReceivedTitle: "BOLs Received",
      eastern: "Eastern",
      central: "Central",
      western: "Western",
      billEnteredTitle: "Bill Entered",
      pendingAudit: "Pending Audit",
      audited: "Audited",
      onHold: "On Hold",
      dashboardError: "Unable to load dashboard metrics",
      dashboardEmpty: "No dashboard data available.",
    },
    cargoClaims: {
      dashboardTitle: "Analyst Dashboard",
      analystFilterLabel: "Analyst",
      cargoClaimEntryButton: "Cargo Claim Entry",
      openClaims: "Open Claims",
      newClaims: "New Claims",
      pending: "Pending",
      inSubrogation: "In Subrogation",
      tabOpenClaims: "Open Claims",
      tabInSubrogation: "In Subrogation",
      tabClosedClaims: "Closed Claims",
      claimsQueueTabsLabel: "Claims queue",
      dashboardError: "Unable to load analyst dashboard metrics",
      dashboardEmpty: "No analyst dashboard data available.",
      validation: {
        invalidPickupDate: "Enter a valid pickup date",
        invalidDeliveryDate: "Enter a valid delivery date",
        deliveryDateMustBeAfterPickup:
          "Delivery Date must be later than Pickup Date",
        amountMismatch: (formattedClaimAmount: string) =>
          `The total of all itemized charges must match the submitted claim amount, ${formattedClaimAmount}.`,
      },
    },
    shipments: {
      noResults: "Uh oh! We couldn't find any shipments.",
      noAccountSelected: "There are no matching results.",
      noAccountSelectedSecondary: "Select an account to view shipments",
      error: "Failed to load shipments.",
    },
    billingEntry: {
      title: "Billing Entry",
      breadcrumbQueue: "Billing Queue",
      breadcrumbEntry: "Bill Entry",
      saveAsDraft: "Save as Draft",
      back: "Back",
      billDone: "Bill Done",
      cancelBill: "Cancel Bill",
      routing: {
        postalCodeMismatchMexico:
          "Postal code mismatch: This code doesn't correspond to the destination in Mexico. Please update the consignee or the shipment tag type",
        postalCodeMismatchCanada:
          "Postal code mismatch: This code doesn't correspond to the destination in Canada. Please update the consignee or the shipment tag type",
        sameAccountError: "Shipper and Consignee cannot be the same account",
        bothInMexicoError: "Both shipper and consignee cannot be in Mexico",
        bothInCanadaError: "Both shipper and consignee cannot be in Canada",
        shipperAndBlindShipperSameError:
          "Shipper and Blind Shipper cannot have the same account and address",
        blindShipperAndConsigneeSameError:
          "Blind Shipper and Consignee cannot be the same account",
      } satisfies Record<string, string>,
      tabs: {
        flagBill: "Flag Bill",
        openBol: "Open BOL",
        blindForm: "Blind Form",
        email: "Email",
        billingRules: "Billing Rules",
      },
      blindShipper: {
        tooltip: "Actual pick-up location",
      },
      blindBillTo: {
        title: "Blind Bill To",
        thirdPartyAccountLabel: "Blind Bill To Account",
        blindBillToggle: "Blind Bill",
      },
      tenderAccount: {
        label: "Tender Account",
        serviceCenterLabel: "Origin Service Center",
        tooltip: "Rate Client",
      },
      tenderBillTo: {
        title: "Tender Bill To",
        thirdPartyAccountLabel: "Tender Bill To Account",
        tenderBillToggle: "Tender Bill",
      },
      hazmat: {
        title: "Hazmat",
        alert: "Hazmat Bill",
        contactInformation: "Contact Information",
        contactName: "Emergency Contact Name*",
        contactPhone: "Emergency Contact Phone*",
        unNaNumber: "UN / NA #*",
        properShippingName: "Proper Shipping Name*",
        hazmatClass: "Hazmat Class*",
        packingGroup: "Packing Group*",
        addPlacard: "Add Placard",
      },
      billingRulesDrawer: {
        title: "Billing Rules",
        empty: "No billing rules apply to this shipment",
        emptySecondary:
          "Billing rules will appear here when conditions are met",
      },
      flagBillDialog: {
        title: "Flag Bill",
        instructionText:
          "Flag this bill by selecting a reason and adding a comment",
        reasonPlaceholder: "Select an option",
        cancel: "Cancel",
        confirm: "Confirm",
        submitting: "Submitting...",
      },
      cancelBillDialog: {
        title: "Cancel Bill",
        message:
          "Are you sure you want to cancel this bill?\n\nOnce canceled, this bill will no longer be processed. You can't undo this action.",
        close: "Close",
        continue: "Continue",
      },
    },
    pickupReview: {
      error: "Failed to load pickup reviews.",
      empty: "No pickups found",
      acceptPickupDialog: {
        title: "Are you sure to Accept?",
        close: "Close dialog",
        warning:
          "The Current Pickup has exceeded the pallet count are you sure to accept this pickup and move to dispatch?",
        reasonLabel: "Reason*",
        reasonRequired: "Reason is required",
        cancel: "Cancel",
        confirm: "Confirm & Accept",
      },
      changeLogDrawer: {
        panelAriaLabel: "Pickup change log panel",
        title: "View Change Log",
        closeAriaLabel: "Close change log",
      },
    },
    allQuotes: {
      loadError: "Unable to load quotes.",
      empty: "No quotes found.",
      actionsColumnHeader: "Actions",
      clearFilters: "Clear filters",
      searchInputAriaLabel: "Search quotes",
    },
    billingQueue: {
      billingQueueTitle: "Billing Queue",
      allBills: "All Bills",
      totalBills: "Total Bills",
      unassigned: "Unassigned",
      completedBills: "Completed Bills",
      billChange: "Bill Change",
      billEntry: "Bill Entry",
      startBilling: "Start Billing",
      assign: "Assign",
      assignBills: "Assign bills",
      selected: "Selected:",
      auditBill: "Audit Bill",
      error: "Failed to load billing queue data.",
      noResults: "Uh oh! We couldn't find any bills.",
      searchPlaceholder: "Search",
      loadingMoreBills: "Loading more bills...",
      filters: {
        billState: "Bill State",
        tag: "Shipment Tags",
        regionTimeZone: "Region Time Zone",
        biller: "Biller",
      },
      columns: {
        proNumber: "PRO #",
        shipmentTag: "Shipment Tag",
        billState: "Bill State",
        puNumber: "PU #",
        regionTimeZone: "Region Time Zone",
        originSic: "Origin SIC",
        shipper: "Shipper",
        consignee: "Consignee",
        weight: "Weight",
        timeInQueue: "Time in the queue",
        biller: "Biller",
      },
    },
    exceptions: {
      exceptionsTitle: "Exceptions",
      poorImageQuality: "Poor Image Quality",
      totalImages: "Total Images",
      images: "Images",
      criticalErrors: "Critical errors",
      totalErrors: "Total Errors",
      errors: "Errors",
      duplicatedPROs: "Duplicated PROs",
      totalDuplicatedPROs: "Duplicated PROs",
      pros: "PROs",
      incorrectPROs: "Incorrect PROs",
      totalIncorrectPROs: "Total PROs",
      noPricingOnFile: "No Pricing on File",
      pricingAlerts: "Pricing Alerts",
      totalCharges: "Total Charges",
      searchPlaceholder: "Search",
      error: "Failed to load exceptions data.",
      noResults: "No exceptions found.",
      columns: {
        tag: "Tag",
        proNumber: "PRO #",
        oldProNumber: "Old PRO #",
        newProNumber: "New PRO #",
        bol: "BOL",
        shipper: "Shipper",
        description: "Description",
        reportedBy: "Reported by",
        reportedDate: "Reported Date",
        originSic: "Origin SIC",
        deliveryDate: "Delivery Date",
        email: "Email",
        status: "Status",
        blockedSince: "Blocked since",
        requestDate: "Request Date",
        aging: "Aging",
        accountExecutive: "Account Executive",
        correctedOn: "Corrected on",
        pickupDate: "Pickup Date",
        date: "Date",
        customerCharges: "Customer Charges",
      },
    },

    ChangeRequests: {
      changeRequestsPageTitle: "Change Requests",
      salesAccommodationRequestsSectionTitle: "Sales Accommodation Requests",
      pendingSarsLabel: "Pending SARs",
      sarRequestsSectionTitle: "SAR Requests",
      sarSearchPlaceholder: "Search",
      sarResolve: "Resolve",
      bcrResolve: "Resolve",
      barResolve: "Resolve",
      billFlagsResolve: "Resolve",
      allQueuesLoadError: "Failed to load all change request queues",
      sarLoadError: "Unable to load sales accommodation requests.",
      sarEmpty: "No SAR requests match your search.",
      sarLoadingMore: "Loading more…",
      sarCaption: "Sales accommodation requests",
      sarColumns: {
        proNumber: "PRO #",
        reasonForAdjustment: "Reason for Adjustment",
        billTo: "Bill To",
        accountExecutive: "Account Executive",
        approvedBy: "Approved by",
        requestDate: "Request Date",
        vpApproval: "VP Approval",
        aging: "Aging",
        sarAmount: "SAR Amount",
        actions: "Action",
      },
      billChangeSectionTitle: "Bill Change Requests",
      pendingBcrsLabel: "Pending BCRs",
      bcrRequestsSectionTitle: "BCR Requests",
      bcrSearchPlaceholder: "Search",
      bcrLoadError: "Unable to load bill change requests.",
      bcrEmpty: "No BCR requests match your search.",
      bcrColumns: {
        proNumber: "PRO #",
        shipper: "Shipper",
        description: "Description",
        accountExecutive: "Account Executive",
        requester: "Requester",
        requestDate: "Request Date",
        shipmentDueDate: "Shipment Due Date",
        aging: "Aging",
      },
      billAdjustmentsSectionTitle: "Bill Adjustments Requests",
      pendingBarsLabel: "Pending BARs",
      barRequestsSectionTitle: "BAR Requests",
      barSearchPlaceholder: "Search",
      barLoadError: "Unable to load bill adjustment requests.",
      barEmpty: "No BAR requests match your search.",
      barColumns: {
        proNumber: "PRO #",
        shipper: "Shipper",
        description: "Description",
        accountExecutive: "Account Executive",
        requester: "Requester",
        requestDate: "Request Date",
        actualDeliveryDate: "Actual Delivery Date",
        aging: "Aging",
      },
      billFlagsSectionTitle: "Bill Flags",
      pendingFlagsLabel: "Pending Flags",
      flagRequestsSectionTitle: "Flag Requests",
      flagSearchPlaceholder: "Search",
      billFlagsLoadError: "Unable to load bill flag requests.",
      billFlagsEmpty: "No flag requests match your search.",
      flagColumns: {
        proNumber: "PRO #",
        originSic: "Orig SIC",
        destSic: "Dest SIC",
        flagDescription: "Flag Description",
        billToName: "Bill to Name",
        accountExecutive: "Account Executive",
        requester: "Requester",
        requestDate: "Request Date",
        actualDeliveryDate: "Actual Delivery Date",
        aging: "Aging",
      },
    },
    fuelScale: {
      empty: "No fuel scales found.",
      error: "Failed to load fuel scales.",
      accounts: {
        dialogTitle: (fuelScaleCode: string) => `${fuelScaleCode} - Accounts`,
        noRecordsFound: "No records found.",
        error: "Failed to load accounts.",
        closeButton: "Close",
        downloadButton: "Download",
        accountNameColumn: "Account Name",
        accountNumberColumn: "Account Number",
      },
      history: {
        downloadButton: "Download",
      },
      criteria: {
        error: "Failed to load criteria.",
        empty: "No criteria found for this fuel scale.",
        optionsError: "Failed to load dropdown options.",
        optionsErrorRetry: "Please close and reopen the dialog to retry.",
      },
    },
    laneMinimum: {
      title: "Lane Minimums",
      scheduleListTitle: "Lane Minimum Schedules",
      error: "Failed to load lane minimum schedules.",
      empty: "No lane minimum schedules found.",
      listTitle: "Lane Minimums",
      listError: "Failed to load lane minimum records.",
      listEmpty: "No lane minimum records found.",
      searchPlaceholder: "Search",
      downloadExcelButton: "Download Excel",
      addNewButton: "Add New",
      columnSequence: "Sequence",
      columnOriginType: "Origin Type",
      columnOriginLocation: "Origin Location",
      columnDestinationType: "Destination Type",
      columnDestinationLocation: "Destination Location",
      columnMinimumCharge: "Minimum Charge",
      columnDirection: "Direction",
      addNewScheduleTitle: "Add New Lane Minimum Schedule",
      editScheduleTitle: "Edit Lane Minimum Schedule",
      duplicateScheduleTitle: "Duplicate Lane Minimum Schedule",
      scheduleDetailsTitle: "Lane Minimum Schedule Details",
      laneMinimumsSectionTitle: "Lane Minimums",
      nameLabel: "Name *",
      descriptionLabel: "Description *",
      originTypeLabel: "Origin Type *",
      originLocationLabel: "Origin location *",
      destinationTypeLabel: "Destination Type *",
      destinationLocationLabel: "Destination location *",
      minimumChargeLabel: "Minimum Charge *",
      oneWayLabel: "One Way",
      bothWaysLabel: "Both Ways",
      addNewLaneButton: "Add New Lane",
      uploadExcelButton: "Upload Excel",
      saveAsDraftButton: "Save as Draft",
      cancelButton: "Cancel",
      saveButton: "Save",
      saveChangesButton: "Save Changes",
      laneSequencePrefix: "Lane Sequence",
    },
    rules: {
      title: "Rules",
      error: "Failed to load rules.",
      empty: "Uh oh! We couldn't find any rules",
      loadingMore: "Loading more rules...",
    },
    rateBases: {
      title: "Rate Bases",
      subtitle: "Manage and configure base rates for pricing calculations",
      error: "Failed to load rate bases.",
      empty: "Uh oh! We couldn't find any rate bases",
      loadingMore: "Loading more rate bases...",
      addNewTitle: "Add New Rate Base",
      editTitle: "Edit Rate Base",
      addNewSubtitle:
        "Choose the rate base type to define how pricing is calculated. Based on your selection, the relevant details and fields will be displayed for configuration.",
      loadError: {
        primary: "Data failed to load",
        secondary:
          "We couldn't load this data. Please refresh the page or try again later.",
      },
      notFound: {
        primary: "Rate base not found.",
        flatRate: "The requested flat rate base does not exist.",
        ratewareXL: "The requested Rateware XL rate base does not exist.",
        commodityRate: "The requested commodity rate base does not exist.",
        truckload: "The requested truckload rate base does not exist.",
        ratePerHandlingUnit:
          "The requested rate per handling unit rate base does not exist.",
        mileageRate: "The requested mileage rate base does not exist.",
      },
      emptyLanes: {
        primary: "No lanes found for this rate base.",
      },
      criteria: {
        error: "Failed to load rate base criteria.",
        empty: "No criteria found for this rate base.",
      },
      view: {
        error: "Failed to load rate base details.",
      },
    },
    highCostZipCode: {
      title: "High Cost Zip Codes",
      scheduleListTitle: "High Cost Zip Codes Schedules",
      error: "Failed to load high cost zip code schedules.",
      empty: "No high cost zip code schedules found.",
      listTitle: "High Cost Zip Codes",
      listSectionAriaLabel: "High cost zip codes list",
      listError: "Failed to load high cost zip code records.",
      listEmpty: "No high cost zip code records found.",
      searchPlaceholder: "Search",
      downloadExcelButton: "Download Excel",
      addNewButton: "Add New",
      addNewScheduleAriaLabel: "Add new high cost zip code schedule",
      addNewZipCodeAriaLabel: "Add new high cost zip code",
      columnSequence: "Sequence",
      columnZipCodeType: "Zip Code Type",
      columnZipCode: "Zip Codes",
      columnState: "State",
      columnRateStructure: "Rate Structure",
      columnRateMinCharge: "Rate (Min. Charge)",
      columnScope: "Scope",
      columnAppliesTo: "Applies to",
      columnScheduleDescription: "Description",
      columnCreatedBy: "Created by",
      columnCreatedDate: "Created Date",
      columnModifiedBy: "Modified by",
      columnModificationDate: "Modification Date",
      statusLabel: "Status",
      activeLabel: "Active",
      activeSwitchAriaLabel: "Active status toggle",
      addNewScheduleTitle: "Add New High Cost Zip Codes Schedule",
      editScheduleTitle: "Edit High Cost Zip Codes Schedule",
      duplicateScheduleTitle: "Duplicate High Cost Zip Codes Schedule",
      scheduleDetailsTitle: "High Cost Zip Codes Schedule Details",
      zipCodesSectionTitle: "High Cost Zip Codes",
      nameLabel: "Name *",
      descriptionLabel: "Description *",
      zipCodeTypeLabel: "Zip Code Type *",
      zipCodeLabel: "Zip Code *",
      lowZipCodeLabel: "Low Zip *",
      highZipCodeLabel: "High Zip *",
      zipCodeRangeLabel: "Zip Code Range *",
      stateLabel: "State *",
      stateRegionLabel: "State/Region *",
      rateStructureLabel: "Rate Structure *",
      rateMinChargeLabel: "Rate/Min. Charge *",
      flatRateFieldLabel: "Flat Rate *",
      ratePer100lbsFieldLabel: "Rate per 100lbs *",
      minChargeFieldLabel: "Min. Charge *",
      surchargePercentageFieldLabel: "Surcharge Percentage *",
      scopeFieldLabel: "Scope *",
      scoreLabel: "Score *",
      appliesToLabel: "Applies to *",
      zipCodeEntryGridAriaLabel: "High cost zip code schedule entries",
      addNewZipCodeButton: "Add New Zip Code",
      uploadExcelButton: "Upload Excel",
      saveAsDraftButton: "Save as Draft",
      cancelButton: "Cancel",
      saveButton: "Save",
      saveChangesButton: "Save Changes",
      zipCodeSequencePrefix: "Zip Code Sequence",
    },
    accessorialCharges: {
      error: "Failed to load accessorial charges.",
      empty: "No accessorial charges found.",
      criteria: {
        error: "Failed to load accessorial charge criteria.",
        empty: "No criteria found for this accessorial charge.",
        optionsError: "Failed to load dropdown options.",
        optionsErrorRetry: "Please close and reopen the dialog to retry.",
      },
      view: {
        error: "Failed to load accessorial charge.",
      },
    },
    accessorialSchedules: {
      error: "Failed to load accessorial schedules.",
      empty: "No accessorial schedules found.",
      saveButton: "Save",
      saveChangesButton: "Save Changes",
      savingLabel: "Saving...",
      savingChangesLabel: "Saving changes...",
      saveAsDraftButton: "Save as Draft",
      savingDraftLabel: "Saving Draft...",
      view: {
        error: "Failed to load accessorial schedule.",
        emptyCharges: "No Selected Accessorial Charges found",
      },
    },
    excelValidation: {
      success: "Excel file validated successfully",
      error: "Failed to validate Excel file",
    },
    globalRates: {
      codeDetailLabels: {
        rateBase: "Rate Base ID",
        fuelSurcharge: "Fuel Surcharge Code",
        accessorialCharge: "Accessorial Charge Schedule Name",
        laneMinimum: "Lane Minimum Schedule Name",
        highCostZipCode: "High Cost Zip Code Schedule Name",
      },
      rateBase: {
        addNewDialogTitle: "Add New Rate Base",
        addNewDialogDescription:
          "Select a Rate Base and set the effective and expiration dates to create a new Rate Base.",
        selectRateBaseLabel: "Select Rate Base",
        newEffectiveDateLabel: "New Effective Date",
        expiringCurrentDateLabel: "Expiring Current Date",
        sequenceLabel: "Sequence",
        selectRateBasePlaceholder: "Select a rate base",
        sequencePlaceholder: "Enter sequence",
        error: "Failed to load rate bases.",
        empty: "No rate bases found.",
      },
      fuelSurcharge: {
        addNewDialogTitle: "Add New Fuel Surcharge",
        addNewDialogDescription:
          "Select a Fuel Surcharge and set the effective and expiration dates to create a new Fuel Surcharge.",
        selectFuelSurchargeLabel: "Select Fuel Surcharge",
        selectFuelSurchargePlaceholder: "Select a fuel surcharge",
        sequenceLabel: "Sequence",
        sequencePlaceholder: "Enter sequence",
        error: "Failed to load fuel surcharges.",
        empty: "No fuel surcharges found.",
      },
      accessorialCharge: {
        addNewDialogTitle: "Add New Accessorial Charge",
        addNewDialogDescription:
          "Select an Accessorial Charge Schedule and set the effective and expiration dates to create a new Accessorial Charge.",
        selectAccessorialChargeLabel: "Select Accessorial Charge Schedule",
        selectAccessorialChargePlaceholder: "Select an accessorial charge",
        error: "Failed to load accessorial charges.",
        empty: "No accessorial charges found.",
      },
      laneMinimum: {
        addNewDialogTitle: "Add New Lane Minimum",
        addNewDialogDescription:
          "Select a Lane Minimum Schedule and set the effective and expiration dates to create a new Lane Minimum.",
        selectLaneMinimumLabel: "Select Lane Minimum Schedule",
        selectLaneMinimumPlaceholder: "Select a lane minimum",
        error: "Failed to load lane minimums.",
        empty: "No lane minimums found.",
      },
      highCostZipCode: {
        addNewDialogTitle: "Add New High Cost Zip Code",
        addNewDialogDescription:
          "Select a High Cost Zip Code Schedule and set the effective and expiration dates to create a new High Cost Zip Code.",
        selectHighCostZipCodeLabel: "Select High Cost Zip Code Schedule",
        selectHighCostZipCodePlaceholder: "Select a high cost zip code",
        error: "Failed to load high cost zip codes.",
        empty: "No high cost zip codes found.",
      },
    },
    fuelAverage: {
      history: {
        dialogTitle: (fuelAverageCode: string) =>
          `History – ${fuelAverageCode}`,
        helperText:
          "This record shows the previous prices of the fuel average code, including dates and modifications.",
        empty: "No history available for this fuel average.",
        error: "Failed to load fuel average history",
        closeButton: "Close",
      },
      modifyPrice: {
        description:
          "We couldn't get the price from the API. You can add it manually.",
      },
    },
    transitTimes: {
      serviceCentersList: {
        loadError: "Failed to load service centers",
        loadErrorSecondary: "Please try again",
      },
      deleteJunctionConfirmationModal: {
        title: (junctionCode: string) =>
          `Delete Junction Sequence ${junctionCode} ?`,
        body: (junctionCode: string) =>
          `You are about to delete this junction (${junctionCode}) from the sequence.\nDeleting this junction will permanently remove its configuration and may affect\ndownstream routing logic.`,
      },
      destJunctions: {
        notFound: "Service center not found",
        notFoundSecondary: "Unable to load destination junctions configuration",
        comingSoon: "Destination junctions coming soon",
        comingSoonSecondary: "Junction configuration will appear here",
        emptyTitle: "No Destination Junctions Available",
        emptySecondary:
          'Click "Add Junction" to connect this Destination Junction to an EOL Service Center.',
        error: "Failed to load destination junctions configuration",
        criteria: {
          error: "Failed to load destination junction criteria.",
          empty: "No criteria found for this destination junction.",
          viewCriteriaDisabledTooltip:
            "Criteria is not added for this junction",
        },
      },
      originJunctions: {
        comingSoon: "Origin junctions coming soon",
        emptyTitle: "No Origin Junctions Available",
        emptySecondary:
          'Click "Add Junction" to connect this Origin Junction to an EOL Service Center.',
        error: "Failed to load origin junction configuration",
        addModalEffectiveDateLabel: "Effective Date",
        addModalExpiryDateLabel: "Expiry Date",
        addModalExpiryHint: "Leave blank for no expiration",
        criteria: {
          optionsError: "Failed to load criteria dropdown options.",
          optionsErrorRetry: "Please close and reopen the dialog to retry.",
          viewCriteriaDisabledTooltip:
            "Criteria is not added for this junction",
          error: "Failed to load origin junction criteria.",
          empty: "No criteria found for this origin junction.",
        },
      },
      crossdock: {
        addJunctionDialogTitle: "Add Crossdock Junction",
        editJunctionDialogTitle: "Edit Crossdock Junction",
        closeCrossdockJunctionDialog: "Close crossdock junction dialog",
        addJunctionPrimaryButton: "Add Junction",
        editJunctionPrimaryButton: "Save Changes",
        junctionCodeLabel: "Junction Code",
        additionalDaysLabel: "Additional Days",
        interchangeCarrierCodeLabel: "Interchange Carrier Code",
        finalDeliveryVendorFieldLabel: "Final Delivery Vendor (SCAC)",
        comingSoon: "Crossdock routes coming soon",
        /** Groups the Crossdock tab title and subtitle for assistive tech and tests. */
        headerSummaryAriaLabel:
          "Crossdock linehaul junction heading and summary",
        tabSubtitle:
          "Non-standard routing where freight transfers between agents",
        emptyTitle: "No Crossdock Junctions Available",
        emptySecondary:
          'Click "Add Junction" to connect this Crossdock Junction to an EOL Service Center.',
        emptyJunctions: "No crossdock junctions configured",
        emptyJunctionsSecondary:
          "Routing and vendor assignment will appear here when configured.",
        addConditionsButton: "Add conditions",
        cancelButton: "Cancel",
        saveButton: "Save",
        addCriteriaButton: "Add Criteria",
        addCriteriaButtonAriaLabel: "Add criteria",
        viewCriteriaDisabledTooltip: "Criteria is not added for this junction",
        serviceLevelsSelected: (count: number) => `${count} selected`,
        criteria: {
          error: "Failed to load crossdock junction criteria.",
          empty: "No criteria found for this crossdock junction.",
        },
      },
      history: {
        comingSoon: "History coming soon",
        comingSoonSecondary: "Service center change history will appear here",
      },
      zipCodes: {
        noMatchingFilter: "No zip codes match your filter",
        noMatchingFilterSecondary: "Try adjusting your filter to see results",
        loadError: "Failed to load zip codes",
        formDataLoadError: "Failed to load form data. Please try again.",
        csvValidationError:
          "Failed to validate CSV file. Please check the file format and try again.",
        bulkImportError: "Failed to import ZIP codes. Please try again.",
        templateDownloadSuccess: "Template downloaded successfully",
        templateDownloadError: "Failed to download template. Please try again.",
        csvUploadFailedTitle: "Upload Failed",
        csvUploadFailedMessage:
          "Something went wrong while uploading the file. Please try again.",
        validation: {
          expiryDateMustBeAfterEffectiveDate:
            "Expiry Date must be later than Effective Date",
        },
        delete: {
          title: "Delete ZIP",
          confirmMessage:
            "You are about to remove ZIP {zipCode} ({city}, {state}) from this Service Center. This action may impact routing and cannot be undone.",
          buttonLabel: "Delete ZIP",
        },
      },
      disruptionCommandCenter: {
        empty: {
          primary: "No Disruption Events Available",
          secondary:
            "No disruption events have been created yet. Create a new event to monitor and manage shipment disruptions.",
        },
      },
      linehaulTransitMatrix: {
        emptyMatrix:
          'No junctions added yet. Click "+ Add Junction" to build your matrix.',
      },
    },
  },
} as const;

export const toastMessage = {
  inbound: {
    changePlanDetails: {
      success: (type: string) => `Plan details ${type} changed`,
    },
    deleteEmptyTrap: {
      success: (title: string, name: string | null) =>
        `Empty trap "${title} ${name ? ": " + name : ""}" deleted`,
    },
    deleteTrap: {
      success: (trapsText: string, trapsCount: number) =>
        `${pluralize("Trap", trapsCount)} ${trapsText} ${
          trapsCount === 1 ? "has" : "have"
        } been deleted`,
    },
    deleteRoute: {
      success: (routesText: string, routesCount: number) =>
        `${pluralize("Route", routesCount)} ${routesText} ${
          routesCount === 1 ? "has" : "have"
        } been deleted`,
    },
    editNotAllowedForPastDates:
      "Only today or future dates allowed for plan changes.",
    editNotAllowedStatus: "Please change the status to enable editing",
    removeEquipmentNotAllowedFromTrapOrRoute: (
      equipment: string,
      type: string
    ) =>
      `Unable to remove the ${equipment}. Change the status to ${type === "Trap" ? "TPLN" : "CPLN"} to remove.`,
    removeDoorNotAllowedFromRoute:
      "Unable to remove the door. Change the status to CPLN to remove.",
    removeDoorFromNotAllowedFromTrap:
      "Unable to remove the door. Change the status to TPLN to remove.",
    removeDoorNotAllowedAfterTrap:
      "Unable to remove the door after TRAP status.",
    removeDoorNotAllowedAfterCldv:
      "Unable to remove the door after CLDV status.",
    removeTrailerOrStraightTruckNotAllowedFromTrapAfterTrap: (
      equipment: string
    ) => `Unable to remove the ${equipment} after TRAP status.`,
    removeTrailerOrStraightTruckNotAllowedFromTrap: (equipment: string) =>
      `Unable to remove the ${equipment}. Change the status to TPLN to remove.`,
    selectOneTrap: "Select at least one trap to delete",
    selectEmptyTraps: "Select only traps with empty shipments to delete",
    selectOneRoute: "Select at least one route to delete",
    selectEmptyRoutes: "Select only routes with empty shipments to delete",
    selectAtLeastOnePlanned: "Select at least one planned record",
    selectAtLeastOneUnplanned: "Select at least one unplanned record",
    removeFreightOnDock: {
      failedCannotRemove:
        "Freight on Dock shipments cannot be removed from the plan",
    },
    removeTrailer: {
      success: (refNumber: string) =>
        `Trailer #${refNumber} removed successfully`,
      error: "Failed to remove trailer. Please try again.",
    },
    repositionTrailer: {
      success: (refNumber: string) =>
        `Trailer #${refNumber} repositioned successfully`,
      error: "Failed to reposition trailer. Please try again.",
    },
    addForeignEquipment: {
      success: (refNumber: string) =>
        `Trailer #${refNumber} added successfully`,
      error: "Failed to add trailer. Please try again.",
    },
    reorder: {
      success: (count: number) =>
        `Shipment${count > 1 ? "s" : ""} reordered successfully!`,
      unableReorderMessage:
        "Unable to reorder a closed route. Move the route to CLDK to reorder.",
      unableToReorderEqualStopNumbers: "Unable to reorder to same stop number.",
    },
    addToPlan: {
      successAutoAndManualAssign: (
        autoAssignShipmentCount: number,
        manualShipmentCount: number
      ) => {
        const autoAssignMessage = `${pluralize(
          "shipment",
          autoAssignShipmentCount,
          true
        )} added to the plan`;

        return manualShipmentCount > 0
          ? `${pluralize(
              "shipment",
              manualShipmentCount,
              true
            )} manually assigned and ${autoAssignMessage}`
          : autoAssignMessage;
      },
      successAdding: (affectedShipmentsCount: number) =>
        `${pluralize(
          "shipment",
          affectedShipmentsCount,
          true
        )} added to the plan`,
      failed:
        "An error occurred while adding shipments to the plan. Please try again.",
      selectOne: "Select at least one trailer to add to the plan",
      emptyTrapSelected:
        "Select only traps with associated shipments to add to the plan",
      plannedTrapSelected: "The selected traps are already part of the plan",
    },
    arrive: {
      success: (tripNumber: number) => `Trip ${tripNumber} marked as arrived`,
      error: "An error occurred while arriving the trip. Please try again.",
    },
    removedFromPlan: {
      success: (shipmentCount: number) =>
        `${pluralize("shipment", shipmentCount, true)}
      removed from plan`,
    },
    assign: {
      success: (affectedShipmentsCount: number) =>
        `${pluralize("shipment", affectedShipmentsCount, true)} reassigned`,
      moveToBucketNotAllowed:
        "Shipments cannot be moved to the appointment bucket",
      noneSelected: "Select at least one record to assign",
      noShipments: "There are no shipments for this item",
      noShipmentsToAssign: "There are no shipments to assign",
      unableToAssignToCancelledTrap:
        "Unable to assign shipment to cancelled trap.",
      unableToAssignToImmutableRoute: (status: string) =>
        `You cannot assign shipments to a DEPT route with ${status} bills.`,
      unableToReassignImmutableRouteShipments: (status: string) =>
        `You cannot reassign shipments from a DEPT route with ${status} bills.`,
      selectReasonToMoveToOsad:
        "The reason code is required to assign shipments to OSAD",
      unableToAssignShipmentToOsadNotInSic: (sicCode: string) =>
        `Shipment cannot be reassigned to OSAD because it is not in ${sicCode}`,
    },
    mergeRoutes: {
      success: (numberOfRoutes: number, routeName: string) =>
        `${numberOfRoutes} routes merged into ${routeName} route`,
      noIndividualShipmentsAllowed:
        "Do not select individual shipments when merging. Select 2 or more routes only",
      selectAtLeastTwo: "Select 2 or more routes to merge",
    },
    mergeRoutesAndTraps: {
      success: (numShipments: number, name: string, isRoute: boolean) =>
        `${pluralize(
          "shipment",
          numShipments,
          true
        )} merged into ${name} (${isRoute ? "route" : "trap"})`,
      selectAtLeastTwo: "Select at least two routes/traps to merge",
      selectMergeDestination: "Select the merge destination",
      selectOnlyNonDispatchedRoutes:
        "Please select only routes with CPLN, CLDK, CLDV statuses to merge",
      selectOnlyRoutesTrapsWithShipments:
        "Select only routes/traps with shipments associated to them",
      noIndividualShipmentsAllowed:
        "Do not select individual shipments when merging. Select 2 or more routes/traps only",
      removeDoorNotAllowedFromRoute:
        "Unable to remove the door when merging. Change the status to CPLN to remove.",
      removeDoorNotAllowedFromTrap:
        "Unable to remove the door when merging. Change the status to TPLN to remove.",
    },
    mergeTraps: {
      success: (numberOfTraps: number, trapName: string, trapTitle: string) =>
        `${numberOfTraps} traps merged into ${trapName} (${trapTitle}) trap`,
      noIndividualShipmentsAllowed:
        "Do not select individual shipments when merging. Select 2 or more traps only",
      selectAtLeastTwo: "Select 2 or more traps to merge",
    },
    autoSequencing: {
      running: "Running auto-sequencing process...",
      success: (count: number) =>
        `${pluralize("route", count, true)} auto-sequenced`,
      failed: (count: number) =>
        `${pluralize("route", count, true)} failed to auto-sequence`,
      someFailed: (successCount: number, failedCount: number) =>
        `${pluralize(
          "route",
          successCount,
          true
        )} successfully auto-sequenced; ${pluralize(
          "route",
          failedCount,
          true
        )} failed.`,
      notAllowedOnTraps: "Traps cannot be reordered",
      notAllowedOnOSAD: "OSAD Shipments cannot be reordered",
      selectAtLeastOneRoute: "Select at least one route",
    },
    print: {
      selectAtLeastOneRoute: "Select at least one route or trap",
      selectAtLeastOneMoreRoute: "Select at least one more route",
      selectAtLeastOneTrap: "Select at least one trap",
      unableToPrintTraps: "The system is unable to print traps",
      selectAtLeastOneTrip: "Select at least one trip",
      noBills: "Select routes or traps with at least 1 bill.",
      routeOrTrapWithNoBills: (routeOrTrap: "route" | "trap") =>
        `Select a ${routeOrTrap} with at least 1 bill.`,
    },
    copyLink: {
      success: "Link copied.",
      error: "Link could not be copied.",
    },
    createTrap: {
      success: (name: string, shipmentsCount: number) =>
        `Trap ${name} with ${pluralize(
          "shipment",
          shipmentsCount,
          true
        )} created`,
      trapNameExists: "Trap name already exists",
      nameRequired: "Trap name is required",
      failed: (errorMessage: string) => `Error creating trap: ${errorMessage}`,
    },
    createRoute: {
      success: (name: string, shipmentsCount: number) =>
        `Route ${name} with ${pluralize(
          "shipment",
          shipmentsCount,
          true
        )} created`,
      routeNameExists: "Route name already exists",
      trailerAlreadyInPlan: "Trailer is already used in today's plan",
    },
    autoAssign: {
      withUnprocessed: (
        numberOfAssignedShipments: number,
        numberOfUnprocessedShipments: number
      ) =>
        `(${numberOfAssignedShipments}) ${pluralize(
          "shipment",
          numberOfAssignedShipments
        )} auto-assigned. (${numberOfUnprocessedShipments}) ${pluralize(
          "shipment",
          numberOfUnprocessedShipments
        )} not processed: already part of this plan.`,
      success: (numberOfAssignedShipments: number) =>
        `(${numberOfAssignedShipments}) ${pluralize(
          "shipment",
          numberOfAssignedShipments
        )} auto-assigned`,
      oneOrMoreFailed:
        "An error occurred. One or more of the shipments failed to auto-assign.",
      failed:
        "An error occurred while auto-assigning to the plan. Please try again.",
      selectShipments: "Select at least one shipment to auto-assign",
    },
    manualAssign: {
      selectionRequiredForManualAssignment:
        "Please select the trailer to enable manual assignment",
      onlyUnplannedShipmentsCanBeManuallyAssigned:
        "Only unplanned shipments can be manually assigned",
      oneOrMoreFailed:
        "An error occurred. One or more of the shipments failed to auto-assign.",
    },
    planDatePicker: {
      dateChanged: "Plan date changed",
      cannotChangePlanDate:
        "Unable to modify the plan date while viewing shipments.",
    },
    createPresetRoute: {
      success: (type: string) => `${type} saved.`,
    },
    updatePresetRoute: {
      success: (type: string) => `${type} edited.`,
    },
    routeStatusChange: {
      selectAtLeastOneRouteOrTrap: "Select at least one route or trap",
      routeTrapNotInPlanStatus: "Route/Trap is not in plan status anymore",
      atLeastOneRouteTrapNotInPlanStatus:
        "At least one Route/Trap is not in plan status anymore",
    },
    incompleteTrailerReinstateToPlan: {
      selectionRequired: "Please select at least one route to reinstate.",
      success: "Selected routes successfully reinstated to plan.",
      error: "There was an error reinstating the selected routes.",
    },
    reconcileShipments: {
      selectAtLeastOneTrap: "Select at least one trap to reconcile",
      selectOnlyOneTrapToReconcile: "Select only one trap to reconcile",
      onlyTrapsCanBeReconciled: "Only traps can be reconciled at this time",
      trapNotInCldkStatus:
        "The selected trap is not in CLDK status. Please ensure the trap is in CLDK status before reconciling.",
      trapShipmentsReconciliationSuccessful: (
        numberOfShipmentsReconciled: number,
        numberOfMovedShipments: number,
        existingTrapName: string,
        newTrapName: string
      ) =>
        `${numberOfShipmentsReconciled} ${pluralize("shipment", numberOfShipmentsReconciled)} reconciled in ${existingTrapName} Trap. ${numberOfMovedShipments} ${pluralize("shipment", numberOfMovedShipments)} moved to new trap: ${newTrapName}.`,
    },
    createCallAttempt: {
      success: "Call attempt logged.",
      otherReason: "Please provide a reason for 'Other' outcome",
    },
  },
  outbound: {
    addTripToPlan: {
      success: (items: Array<number>) => {
        if (items.length === 1) {
          return `Trip ${items[0]} added to plan.`;
        } else {
          return `${items.length} trips added to plan.`;
        }
      },
      error: (items: Array<number>) =>
        `Failed to add ${items} ${pluralize("trip", items.length)} to plan.`,
    },
    removeTripFromPlan: {
      success: (items: Array<number>) => {
        if (items.length === 1) {
          return `Trip ${items[0]} removed from plan.`;
        } else {
          return `${items.length} trips removed from plan.`;
        }
      },
      error: (items: Array<number>) =>
        `Failed to remove ${items} ${pluralize("trip", items.length)} to plan.`,
    },
    reconcileShipments: {
      success: (items: number) =>
        `${pluralize("shipment", items, true)} reconciled.`,
      error: (items: number) =>
        `Failed to reconcile ${pluralize("shipment", items, true)}.`,
    },
    mergeLanes: {
      success: (lanes: string[], mergeLane: string) =>
        `${lanes.join(", ")} merged into lane ${mergeLane}.`,
      error: (items: number) =>
        `Failed to merge ${pluralize("lane", items, true)}.`,
      information: "Select two or more lanes to merge",
    },
    mergeDoors: {
      success: (doorNumbers: number[], mergedIntoDoorNumber: number) =>
        `Doors ${doorNumbers.join(", ")} merged into door ${mergedIntoDoorNumber}.`,
      error: (numberOfDoors: number) =>
        `Failed to merge ${pluralize("door", numberOfDoors, true)}.`,
      information: "Select two or more doors to merge",
    },
    transformToTrap: {
      success: (routeName: string) => `${routeName} trap created.`,
      error: "Failed to create trap.",
    },
    removeShipments: {
      bulkSuccess: (count: number) =>
        `${pluralize("shipment", count, true)} removed from trailer.`,
      bulkError: "Failed to remove shipments from trailer.",
      success: (proNumber: string) =>
        `Pro # ${proNumber} unreconciled from trailer.`,
      error: (proNumber: string) =>
        `Pro # ${proNumber} failed to remove from trailer.`,
      title: (proNumber: string) =>
        `Undo reconciliation for Pro # ${proNumber}`,
      question: "Are you sure you want to undo reconciliation for ",
      information:
        "This will change the shipment to its original status only. To move this shipment, please use the lane details page.",
    },
    generatePdf: {
      error: "Failed to generate.",
      success: "Manifest printed successfully.",
    },
    assignAsset: {
      success: "Assets added.",
      update: "Assets updated.",
      error: "Failed to assign assets.",
    },
    assignTrapAsset: {
      success: (trapName: string) => `'${trapName}' created.`,
      update: (trapName: string) => `'${trapName}' updated.`,
      error: "Failed to assign asset.",
    },
    removeAsset: {
      success: "Assets removed successfully.",
      error: "Failed to remove assets.",
    },
    reassignDoor: { success: "Door reassigned successfully." },
    trailerHub: {
      deleteLoadNote: {
        success: "Note deleted",
        error: "Failed to delete note",
      },
      updateLoadNote: {
        success: "Note updated",
        error: "Failed to update note",
      },
      createLoadNote: {
        success: "Note created",
        error: "Failed to create note",
      },
    },
    changeTrailerVisibility: {
      hide: (trailerNumber: string) =>
        `Trailer ${trailerNumber} moved to hidden`,
      showTrips: (trailerNumber: string) =>
        `Trailer ${trailerNumber} moved to trips`,
      showTraps: (trailerNumber: string) =>
        `Trailer ${trailerNumber} moved to traps`,
      exclude: (shipmentCount: number) =>
        `${pluralize("shipment", shipmentCount, true)} excluded from the plan`,
      include: (shipmentCount: number) =>
        `${pluralize("shipment", shipmentCount, true)} included on the plan`,
    },
    swingTrailer: {
      success: (trailerNumber: string) =>
        `Swing confirmed. Shipments moved to Trailer ${trailerNumber}`,
    },
  },
  linehaul: {
    comments: {
      delete: {
        success: "Comment deleted successfully",
        error: "Failed to delete comment",
        permissionError: "You can only delete your own comments",
      },
      update: {
        success: "Comment updated successfully",
        error: "Failed to update comment",
        permissionError: "You can only edit your own comments",
      },
      create: {
        success: "Comment added successfully",
        error: "Failed to add comment",
      },
      resolve: {
        success: "Comment resolved successfully",
        error: "Failed to resolve comment",
      },
      unresolve: {
        success: "Comment unresolved successfully",
        error: "Failed to unresolve comment",
      },
      resolution: {
        update: {
          success: "Resolution updated successfully",
          error: "Failed to update resolution",
          permissionError: "You can only edit your own resolutions",
        },
      },
    },
    calculateRollover: {
      failed: "Uh oh! There was an issue calculating the rollover fee.",
    },
    cancelTrip: {
      success: (type: "schedule" | "trip") => `Cancelled ${type}`,
      failed: (type: "schedule" | "trip") => `Failed to cancel ${type}`,
    },
    closeTrip: {
      success: (tripNumber: number) =>
        `The trip #${tripNumber} was closed successfully`,
      failed: "There was an error cancelling the trip",
    },
    addTripToFavorites: {
      success: "Trip added to favorites successfully",
      error: "Failed to add trip to favorites",
    },
    removeTripFromFavorites: {
      success: "Trip removed from favorites successfully",
      error: "Failed to remove trip from favorites",
    },
    notifications: {
      markRead: {
        success: "Notification marked as read",
        error: "Failed to mark notification as read",
      },
      markUnread: {
        success: "Notification marked as unread",
        error: "Failed to mark notification as unread",
      },
      markAllAsRead: {
        success: "All notifications marked as read",
        error: "Failed to mark all notifications as read",
      },
    },
    requestStatus: {
      success: "Status request sent successfully",
      error: "Failed to send status request",
    },
    changeTripStatus: {
      success: (tripNumber: number) =>
        `The trip #${tripNumber} was updated successfully`,
      failed: "There was an error updating the trip status",
    },
    updateTripStatus: {
      success: "Trip status updated successfully",
      error: "Failed to update trip status",
      noStatusChange: "Please select a different status to update the trip",
    },
    enrouteTrip: {
      success: (tripNumber: number) => `Trip #${tripNumber} set to en route`,
      failed: "There was an error en routing the trip",
      reasonForDelay:
        "Delay reason cannot be provided as the departure time is earlier than the scheduled gate time",
    },
    createSchedule: {
      success: "Schedule created",
      failed: "Failed to create schedule",
    },
    publishSchedule: {
      success: "Schedules published",
      failed: "Failed to publish schedules",
    },
    reinstateSchedule: {
      success: "Reinstated schedule",
      failed: "Failed to reinstate schedule",
    },
    unpublishTrip: {
      success: "Unpublished trip",
      failed: "Failed to unpublish trip",
    },
    deleteScheduleNote: {
      success: "Note deleted",
      failed: "Failed to delete note",
    },
    updateScheduleNote: {
      success: "Note updated",
      failed: "Failed to update note",
    },
    createScheduleNote: {
      success: "Note created",
      failed: "Failed to create note",
    },
    publishTrip: {
      success: "Trip published",
      failed: "Failed to publish trip",
    },
    updateTripPriority: {
      success: "Trip priority updated",
      failed: "Failed to update trip priority",
    },
    editTemplate: {
      errorLoading: "Error loading entry",
    },
    updateTrip: {
      success: "Trip updated successfully",
      failed: "Failed to update trip",
    },
    updateSchedule: {
      success: "Schedule updated successfully",
      failed: "Failed to update schedule",
      notFound: "Schedule couldn't be found",
    },
    updateCarrierContacts: {
      success: (carrierCode: string) => `Updated contacts for ${carrierCode}`,
      failed: (carrierCode: string) =>
        `Failed to update contacts for ${carrierCode}`,
    },
    deleteMasterSchedule: {
      success: (week: Dayjs) =>
        `Deleted master schedule for week of ${week.format(
          DaylightDateFormat.ISO_DATE
        )}`,
      failed: (week: Dayjs) =>
        `Failed to delete master schedule for week of ${week.format(
          DaylightDateFormat.ISO_DATE
        )}`,
    },
    loadPlanReport: {
      info: "Generating report...",
      error: "Uh oh! There was an issue generating your pdf.",
    },
    yardCheckFileUpload: {
      fileSizeError: "File size must be less than 5MB",
      fileTypeError:
        "Please select a valid file type (PDF, DOC, DOCX, XLSX, CSV)",
      uploadSuccess: "Yard check file uploaded successfully!",
      uploadFailed: "Failed to upload file",
      unexpectedError: "An unexpected error occurred",
    },
    yardCheckFileDelete: {
      deleteSuccess: "Yard check file deleted successfully",
      deleteFailed: "Failed to delete the yard check file",
    },
    yardCheckFileDownload: {
      downloadSuccess: "Yard check file downloaded successfully",
      downloadFailed: "Failed to download yard check file",
    },
    sendBulkRequest: {
      success: "Bulk request sent successfully",
      error: "Failed to send bulk request",
    },
    masterSchedule: {
      createMasterScheduleSuccess: "Created master schedule",
      createMasterScheduleError: "Failed to create master schedule",
      createTemplateSuccess: (name: string) => `Created template "${name}"`,
      createTemplateError: "Failed to create template",
      updateTemplateSuccess: "Updated template",
      updateTemplateError: "Failed to update template",
      deleteTemplateSuccess: (name: string) => `Deleted template "${name}"`,
      deleteTemplateError: "Failed to delete template",
      createEntrySuccess: "Created entry",
      createEntryError: "Failed to create entry",
      updateEntrySuccess: "Updated entry",
      updateEntryError: "Failed to update entry",
      deleteEntrySuccess: "Deleted entry",
      deleteEntryError: "Failed to delete entry",
      publishSuccess: (excludedCount?: number) =>
        `Published master schedule entries${
          excludedCount ? ` (excluded ${excludedCount})` : ""
        }`,
      publishError: "Failed to publish master schedule entries",
      expireEntrySuccess: "Expired entry",
      expireEntryError: "Failed to expire entry",
      errorLoading: "Error loading entry",
    },
    assignShipments: {
      success: (count: number, tripNumber: number) =>
        `${pluralize("Shipments", count, true)} assigned to Trip# ${tripNumber}`,
      error: (count: number, tripNumber: number) =>
        `Failed to assign ${pluralize("Shipments", count, true)} to trip #${tripNumber}`,
      successBulk: (
        count: number,
        fromTripNumber: number,
        toTripNumber: number
      ) =>
        `${pluralize("shipment", count, true)} removed from Trip# ${fromTripNumber} and assigned to Trip# ${toTripNumber}`,
    },
    unassignShipments: {
      success: (count: number) =>
        `${pluralize("Shipments", count, true)} unassigned`,
      error: (count: number) =>
        `Failed to unassign ${pluralize("Shipments", count, true)}`,
    },
    loadAndHold: {
      success: "Load & Hold status updated successfully",
      error: "Failed to update Load & Hold status",
    },
    generateCBOL: {
      success: "CBOL generated successfully",
      error: "Failed to generate CBOL",
    },
    shipmentDocument: {
      downloadSuccess: "Document downloaded successfully.",
      printSuccess: "Document opened for printing.",
    },
    updateTripCBOLInfo: {
      success: "Estimated departure time set",
      pending: "CBOL generating...",
      error: "Failed to update CBOL information",
    },
    ratesAndAccessorial: {
      accessorials: {
        success: "The accessorials was uploaded successfully",
        error: "The accessorials failed to upload",
      },
      addNewRate: {
        success: "The rate was created successfully.",
        error: "Failed to create rate.",
      },
      editRate: {
        success: "The rate was updated successfully.",
        error: "Failed to update rate.",
      },
      deleteRate: {
        success: "The rate was deleted successfully.",
        error: "Failed to delete rate.",
      },
      pricingContact: {
        success: "Pricing contact updated successfully.",
        error: "Failed to update pricing contact.",
      },
      caFsc: {
        success: "CA FSC updated successfully.",
        error: "Failed to update CA FSC.",
      },

      tolls: {
        createSuccess: "Toll added successfully.",
        createError: "Failed to add toll.",
        updateSuccess: "Toll updated successfully.",
        updateError: "Failed to update toll.",
      },

      downloadAllLRates: {
        success: "All L rates downloaded successfully.",
        error: "Failed to download all L rates. Please try again.",
      },
    },

    ucccOfd: {
      serviceFailure: {
        success: "Service failure status updated successfully",
        error: "Failed to update service failure status",
        deleteSuccess: "Service failure status removed successfully",
        deleteError: "Failed to remove service failure status",
      },
      assignOwner: {
        assignSuccess: (ownerName: string) => `Owner ${ownerName} assigned`,
        unassignSuccess: "Shipments unassigned successfully",
        error: "Failed to assign owner",
      },
      serviceFailureRecapDownload: {
        success: "Report downloaded successfully.",
        error: "Failed to download report. Please try again.",
      },
      expedite: {
        success: (proNumber: string) =>
          `Expedite status added for Pro# ${proNumber}`,
        error: (proNumber: string) =>
          `Failed to add expedite status for Pro# ${proNumber}`,
        updateSuccess: (proNumber: string) =>
          `Expedite status updated for Pro# ${proNumber}`,
        updateError: (proNumber: string) =>
          `Failed to update expedite status for Pro# ${proNumber}`,
        deleteSuccess: (proNumber: string) =>
          `Expedite status removed for Pro# ${proNumber}`,
        deleteError: (proNumber: string) =>
          `Failed to remove expedite status for Pro# ${proNumber}`,
      },
    },
  },
  settings: {
    createRouteTrap: {
      failed:
        "Could not create route/trap. The Zip Codes are already assigned to another Route/trap.",
      routeNameAlreadyExists: "Route name already exists",
      trapNameAlreadyExists: "Trap name already exists",
    },
    editRouteTrap: {
      failed:
        "Could not edit route/trap. The Zip Codes are already assigned to another Route/trap.",
    },
    editDriver: {
      failed: "There was an error saving the driver changes. Please try again",
      success: "Driver updated.",
    },
    equipments: {
      create: {
        warning: {
          duplicatedTrailerNumber: "Trailer Number already exists.",
        },
      },
    },
    moveZipCode: {
      success: (sourceRoute: string, destinationRoute: string) =>
        `Zip code  moved from ${sourceRoute} to ${destinationRoute}.`,
      failed: () => "Failed to move Zip Code.",
    },
    editZipCode: {
      success: (zipCode: string) => `Zip code ${zipCode} updated.`,
      failed: "Something went wrong while updating the zip code.",
    },
    deleteZipCode: {
      success: "Zip code deleted.",
      failed: "Something went wrong while deleting the zip code.",
    },
    createZipCode: {
      success: (zipCode: string) => `Zip code ${zipCode} created.`,
      error: "Something went wrong while creating the zip code.",
    },
    oldMoveZipCode: {
      success: (
        zipCode: string,
        sourceRoute: string,
        destinationRoute: string
      ) =>
        `Zip code ${zipCode} moved from ${sourceRoute} to ${destinationRoute}.`,
      failed: (zipCode: string) => `Failed to move Zip Code ${zipCode} .`,
    },
    user: {
      get: {
        error: () => "User not found.",
      },
      toggleActive: {
        success: (name: string, active: boolean) =>
          `${name} is set to ${active ? "active" : "inactive"}`,
      },
      removeGroup: {
        success: (numGroups: number) =>
          numGroups > 1
            ? `${numGroups} groups removed`
            : `${numGroups} group removed`,
        successWithName: (groupName: string) => `${groupName} group removed`,
      },
      updateGroup: {
        success: (name: string) => `Updated group ${name}`,
      },
    },
    groups: {
      create: {
        success: (name: string) => `Group ${name} created`,
        failed: (errorMessage: string, name: string) =>
          `Error "${errorMessage}" while trying to create Group ${name}`,
      },
      delete: {
        success: (name?: string) =>
          name ? `${name} group deleted` : "Group deleted",
        failed: (name?: string) =>
          name
            ? `Error while trying to delete ${name} group`
            : "Error while trying to delete group",
      },
      toggleActive: {
        success: (name: string, active: boolean) =>
          `${name} Group is set to ${active ? "active" : "inactive"}`,
        failed: (name: string, active: boolean) =>
          `Error while trying to set ${name} Group to ${
            active ? "active" : "inactive"
          }`,
      },
      update: {
        success: (name: string) => `${name} Group updated.`,
        failed: (name: string) => `Error while trying to update Group ${name}`,
      },
      removeUser: {
        success: (userName: string) => `${userName} removed from group`,
        failed: (userName: string) =>
          `Error while trying to remove ${userName} from group`,
      },
    },
    permissions: {
      update: {
        success: () => "Permission updated.",
        failed: () => "Error while trying to update Permission",
      },
      create: {
        success: () => "Permission created",
        failed: (errorMessage: string) =>
          `${errorMessage} Error while trying to create Permission`,
      },
    },
    modules: {
      toggleActive: {
        success: (name: string, active: boolean) =>
          `${name} Module is set to ${active ? "active" : "inactive"}`,
        failed: (name: string, active: boolean) =>
          `Error while trying to set ${name} Module to ${
            active ? "active" : "inactive"
          }`,
      },
      deleteModule: {
        success: (name: string) => `${name} Module deleted.`,
        failed: (name: string) =>
          `An error occurred while deleting the ${name} Module. Please try again`,
      },
      createModule: {
        success: (name: string) => `Module ${name} created.`,
        failed: (errorMessage: string, name: string) =>
          `Error "${errorMessage}" while creating the Module ${name}. Please try again`,
      },
      deletePermission: {
        success: "Permission deleted.",
        failed:
          "Error while trying to delete the permission. Please try again later.",
      },
    },
    equipmentUpdated: {
      success: "Equipment saved",
      failed: "Failed to save equipment",
    },
    equipmentDeleted: {
      success: "Equipment deleted",
    },
    createZone: {
      success: (zone: string) => `Zone ${zone} created successfully`,
      failed: (errorMessage: string) =>
        `Could not create the zone. ${errorMessage}`,
      invalidName: (minZoneNameLength: number) =>
        `Zone name must not exceed ${minZoneNameLength} characters.`,
    },
    updateZone: {
      success: (zone: string) => `Zone ${zone} updated successfully`,
      failed: (errorMessage: string) =>
        `Could not update the zone. ${errorMessage}`,
    },
    deleteZone: {
      failed: "An error occurred while deleting the zone. Please try again.",
    },
    deleteTrapPreset: {
      success: "Deleted Preset Lane",
      failed: (error: unknown) => `Failed to delete Preset Lane ${error}`,
    },
    roleUpdate: {
      success: "Role saved successfully",
    },
    deleteLoadTemplate: {
      success: "Deleted Static Load Template",
      failed: (error: unknown) =>
        `Failed to delete Static Load Template ${error}`,
    },
    duplicateLoadTemplate: {
      success: "Duplicated Static Load Template",
      failed: (error: unknown) =>
        `Failed to duplicate Static Load Template ${error}`,
    },
    defaultLoadTemplate: {
      success: (name?: string) => `${name} set as Default`,
      failed: (error: unknown) =>
        `Failed to set Default Static Load Template ${error}`,
    },
    updateLoadTemplate: {
      success: "Updated Default Load Template",
      failed: (error: unknown) =>
        `Failed to update Default Load Template ${error}`,
    },
    addPresetLane: {
      success: "Added Preset Lane",
      failed: (error: unknown) => `Failed to create Preset Lane ${error}`,
    },
    updatePresetLane: {
      success: "Updated Preset Lane",
      failed: (error: unknown) => `Failed to update Preset Lane ${error}`,
    },
    deleteRoute: {
      success: (type: "Route" | "Trap", name: string) =>
        `${name} ${type} deleted`,
      failed: (type: "Route" | "Trap", name: string) =>
        `Failed to delete ${type} ${name}`,
    },
    createRegion: {
      failed: "Failed to create region",
    },
    updateRegion: {
      failed: "Failed to update region",
    },
  },
  shared: {
    lockRoutes: {
      success: (count: number, isChecked: boolean) =>
        `${count === 1 ? "Route" : count + " routes"} ${isChecked ? "un" : ""}locked`,
    },
    massageRoute: {
      success: (routeName: string, isPlanCompleted: boolean) =>
        isPlanCompleted
          ? `Route ${routeName} marked as done`
          : `Route ${routeName} marked as not done`,
      failed: "Failed to mark route as done",
      tooltipText: (isPlanCompleted: boolean) =>
        !isPlanCompleted ? "Mark as done" : "Mark not done",
    },
    drivers: {
      failedFetch:
        "An error occurred while fetching drivers. Please try again.",
    },
  },
  generics: {
    error: "Sorry, an error occurred",
    failedToLoadData: "Data failed to load",
    failedToLoadDataSubtext:
      "We couldn’t load this data. Please refresh the page or try again later.",
    fieldUpdated: (field: string) => `${field} updated successfully.`,
    failedValidation: "Please complete all required fields",
    failedFetch: (name: string) =>
      `An error occurred while fetching ${name}. Please try again.`,
    handleError: (message: string, error: Error) => `${message} ${error}`,
    copied: "Copied.",
    copiedField: (field: string) => `${field} copied.`,
    copyFailed: "Failed to copy.",
  },
  dispatch: {
    routes: {
      updateStatus: {
        success: "Route status updated",
        error: "Error while updating route status",
      },
      createPickupRoute: {
        success: (routeName: string) => `Pickup route ${routeName} created`,
      },
      editRoute: {
        success: "The route has been successfully edited",
      },
      reorderStops: {
        success: "Route sequence updated",
        discard: "Changes discarded",
        error: "Failed to reorder stops",
      },
    },
    stops: {
      routeInfoMissing: (stopName: string, status: string) =>
        `Route information missing for ${status} stop ${stopName}.`,
      assignStopToRoute: {
        success: (routeName: string, driverName: string) =>
          `Stop assigned to ${routeName} - ${driverName}`,
        error: "Assignment failed",
      },
      assignToMatchingRoute: {
        title: (routeName: string) => `Assign to ${routeName}?`,
        message:
          "This stop will be merged with the existing stop at this address on the route.",
        confirmLabel: (routeName: string) => `Assign to ${routeName}`,
        confirmButtonLoadingLabel: "Assigning...",
        disabledTooltip: "Route must be dispatched to assign stops",
      },
      mergeStopIntoRoute: {
        success: (routeName: string, driverName: string) =>
          `Stop merged into ${routeName} - ${driverName}`,
        successGeneric: "Stop merged",
        error: "Merge failed",
      },
      reassignStopToRoute: {
        success: (routeName: string, driverName: string) =>
          `Stop reassigned to ${routeName} - ${driverName}`,
        error: "Reassignment failed",
      },
      unassignStopFromRoute: {
        success: "Stop unassigned",
        error: "Error while unassigning stop",
      },
      updateStopSic: {
        success: (sicCode: string) => `Stop moved to ${sicCode} Service Center`,
        error: "Error while updating stop Service Center",
      },
      reschedule: {
        success: (stopName: string) => `Stop ${stopName} rescheduled`,
        error: (stopName: string) => `Error while rescheduling ${stopName}`,
      },
      splitStopToUnassigned: {
        success: "Shipments have been marked as unassigned",
        error: "Error while splitting stop",
      },
      splitStopToRoute: {
        success: (shipmentQuantity: number, routeName: string) =>
          `${shipmentQuantity} shipments added to ${routeName}`,
        error: "Error while splitting stop",
      },
      multiAssignStopsToRoute: {
        success: (stopCount: number, routeName: string) =>
          `${pluralize("stop", stopCount, true)} assigned to ${routeName}`,
        partial: (
          successCount: number,
          failedCount: number,
          routeName: string
        ) =>
          `${pluralize("stop", successCount, true)} assigned to ${routeName}, ${failedCount} failed`,
        error: "Failed to assign stops to route",
      },
      markStopAsIncomplete: {
        success: (stopName: string) => `Stop ${stopName} marked as incomplete`,
        error: "Failed to mark stop as incomplete",
      },
      completeStop: {
        success: (stopName: string) =>
          `Stop ${stopName} completed successfully`,
        error: "Failed to complete stop",
      },
    },
    realtime: {
      error: "Failed to decode websocket message.",
    },
  },
  backOffice: {
    pickupEntry: {
      sendPickupDetails: {
        success: "Pickup details sent successfully",
        error: "Failed to send pickup details",
      },
      sendBlindShipment: {
        success: "Blind shipment form sent successfully",
        error: "Failed to send blind shipment form",
      },
      sendBol: {
        success: "Bill of Lading sent successfully",
        error: "Failed to send Bill of Lading",
      },
      sendShippingLabels: {
        success: "Shipping labels sent successfully",
        error: "Failed to send shipping labels",
      },
      sendMail: {
        success: "Quotation sent successfully",
        error: "Failed to send quotation",
      },
    },
    quote: {
      saved: "Quote saved",
      duplicated: "Quote Duplicated",
      downloaded: "Quote successfully downloaded",
    },
    shipments: {
      export: {
        success: (count: number) =>
          count === 1
            ? "Shipment exported successfully"
            : "Shipments exported successfully",
        error: "Failed to export shipments",
      },
    },
    fuelPrice: {
      updateSuccess: "Fuel price updated successfully",
      updateFailed: "Failed to update fuel price",
      retrySuccess: "Fuel price fetched successfully",
      retryFailed: "Failed to fetch fuel price",
      downloadSuccess: "Fuel price history downloaded successfully",
      downloadError: "Failed to download fuel price history",
    },
    billingQueue: {
      assignSuccess: "Bills assigned successfully",
      assignFailed: "Failed to assign bills",
    },
    accessorialSchedules: {
      createSuccess: "Accessorial schedule created successfully",
      updateSuccess: "Accessorial schedule updated successfully",
      draftSuccess: "Draft saved successfully",
      status: {
        success: "Accessorial schedule status updated successfully",
        error: "Failed to update accessorial schedule status",
      },
      accounts: {
        export: {
          success: "Accounts exported successfully",
          error: "Failed to export accounts",
        },
      },
    },
    fuelScale: {
      create: {
        success: "Fuel scale created successfully",
        error: "Failed to create fuel scale",
      },
      update: {
        error: "Failed to update fuel scale",
      },
      draft: {
        success: "Draft saved successfully",
      },
      status: {
        update: {
          success: "Fuel scale status updated successfully",
          error: "Failed to update fuel scale status",
        },
      },
      validation: {
        codeRequired: "Fuel Scale Code is required",
        codeAlreadyExists: "Fuel Scale Code already exists",
      },
      rates: {
        downloadSuccess: "Fuel rates downloaded successfully",
        downloadError: "Failed to download fuel rates",
      },
      accounts: {
        export: {
          success: "Accounts exported successfully",
          error: "Failed to export accounts",
        },
      },
      history: {
        downloadSuccess: "Fuel scale history downloaded successfully",
        downloadError: "Failed to download fuel scale history",
      },
      fuelSurcharge: {
        rates: {
          excelValidation: {
            success: "Excel file validated successfully",
            error: "Failed to validate Excel file",
          },
        },
      },
    },
    globalRates: {
      rateBase: {
        create: {
          success: "Rate base created successfully",
          error: "Failed to create rate base",
        },
      },
    },
    laneMinimum: {
      excelValidation: {
        success: "Excel uploaded successfully",
        error: "Failed to validate Excel file",
      },
      create: {
        success: "Lane minimum schedule created successfully",
        error: "Failed to create lane minimum schedule",
      },
      draft: {
        success: "Lane minimum schedule saved as draft successfully",
      },
      status: {
        success: "Lane minimum schedule status updated successfully",
        error: "Failed to update lane minimum schedule status",
      },
      edit: {
        success: "Lane minimum schedule updated successfully",
        error: "Failed to update lane minimum schedule",
      },
      validation: {
        nameRequired: "Name is required to save as draft",
        duplicateLane: "This lane sequence is duplicated",
      },
      download: {
        success: "Lane minimums downloaded successfully",
        error: "Failed to download lane minimums",
      },
    },
    rateBases: {
      create: {
        success: "Rate base created successfully",
        error: "Failed to create rate base",
      },
    },
    accessorialCharge: {
      create: {
        success: "Accessorial charge created successfully",
        error: "Failed to create accessorial charge",
      },
      update: {
        success: "Accessorial charge updated successfully",
        error: "Failed to update accessorial charge",
      },
      draft: {
        success: "Accessorial charge saved as draft successfully",
      },
      validation: {
        codeRequired: "Code is required to save as draft",
        duplicateRateSequence: "This rate sequence is duplicated",
      },
    },
    highCostZipCode: {
      excelValidation: {
        success: "Excel uploaded successfully",
        error: "Failed to validate Excel file",
      },
      status: {
        success: "High cost zip code schedule status updated successfully",
        error: "Failed to update high cost zip code schedule status",
      },
      create: {
        success: "High cost zip code schedule created successfully",
        error: "Failed to create high cost zip code schedule",
      },
      update: {
        success: "High cost zip code schedule updated successfully",
        error: "Failed to update high cost zip code schedule",
      },
      validation: {
        nameRequired: "Name is required to save as draft",
        duplicateZipCode: "This zip code sequence is duplicated",
      },
      draft: {
        success: "High cost zip code schedule saved as draft successfully",
      },
      download: {
        success: "High cost zip codes downloaded successfully",
        error: "Failed to download high cost zip codes",
      },
      downloadSchedule: {
        success: "High cost zip code schedules downloaded successfully",
        error: "Failed to download high cost zip code schedules",
      },
    },
    transitTimes: {
      addServiceCenter: {
        success: "Service center created successfully",
        error: "Failed to create service center",
        duplicateScCode: (scCode: string) =>
          `Service center with code '${scCode}' already exists`,
      },
      editServiceCenter: {
        success: "Service center updated successfully",
        error: "Failed to update service center",
      },
      serviceCenterDetails: {
        loadError: "Failed to load service center details",
        loadErrorSecondary:
          "Please try again or contact support if the issue persists",
      },
      originJunction: {
        createSuccess: "Origin junction created successfully",
        updateSuccess: "Origin junction updated successfully",
        updateError: "Failed to update origin junction",
        createError: "Failed to create origin junction",
        deleteSuccess: "Origin junction deleted successfully",
        deleteError: "Failed to delete origin junction",
      },
      destinationJunction: {
        createSuccess: "Destination junction created successfully",
        createError: "Failed to create destination junction",
        updateSuccess: "Destination junction updated successfully",
        updateError: "Failed to update destination junction",
        deleteSuccess: "Destination junction deleted successfully",
        deleteError: "Failed to delete destination junction",
      },
      crossdockJunction: {
        create: {
          success: "Crossdock junction created successfully",
          error: "Failed to create crossdock junction",
        },
        update: {
          success: "Crossdock junction updated successfully",
          error: "Failed to update crossdock junction",
        },
        deleteSuccess: "Crossdock junction deleted successfully",
        deleteError: "Failed to delete crossdock junction",
      },
      zipCodes: {
        export: {
          success: "ZIP codes exported successfully",
          error: "Failed to export ZIP codes",
        },
        delete: {
          success: "ZIP code deleted successfully",
          error: "Failed to delete ZIP code",
        },
        create: {
          success: (zipCode: string) =>
            `ZIP code ${zipCode} created successfully`,
          error: "Failed to create ZIP code",
        },
        update: {
          error: "Failed to update ZIP code",
        },
        bulkExpire: {
          success: "Bulk expire completed successfully",
          error: "Failed to expire ZIP codes",
        },
        bulkPaste: {
          error: "Failed to bulk add ZIP codes",
        },
      },
    },
  },
  tripList: {
    driverCheckIn: {
      success: (bookingNumber: string) =>
        `Check-in for Booking # ${bookingNumber} complete`,
    },
  },
} as const;
